import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  GridPageChangeEvent,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { getSelectedState } from "@progress/kendo-react-data-tools";
import { Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { LoadingPanel } from "../../components/layout/Loading";
import { ErrorToast } from "../../components/toast/Toasts";
import { getAllActiveStates } from "../state/services/state.services";
import {
  getAllActiveEmailTemplate,
  getAllEmailTemplate,
} from "../emailTemplate/services/emailTemplate.services";
import {
  findContactsByState,
  sendBulkEmail,
} from "./services/bulkEmail.services";
import { clearBulkEmailState } from "./bulkEmailSlice";
import { ISendBulkEmailPayload } from "./bulkEmailModel";
import { checkAcessRights } from "../../_helper/helper";
import { INSERT_ACCESS } from "../../_contstants/common";

import { BulkEmailHeader } from "./components/BulkEmailHeader";
import { ContactSelectionSection } from "./components/ContactSelectionSection";
import { EmailComposerCard } from "./components/EmailComposerCard";
import { EmailLivePreview } from "./components/EmailLivePreview";

const DATA_ITEM_KEY = "ID";
const idGetter = getter(DATA_ITEM_KEY);

const options = [
  { label: "[name]", value: "[name]" },
  { label: "[mobileNumber]", value: "[mobileNumber]" },
  { label: "[emailID]", value: "[emailID]" },
  { label: "[referalCode]", value: "[referalCode]" },
  { label: "[cityName]", value: "[cityName]" },
];

const BulkEmail: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const gridRef = useRef<any>(null);

  const isSendAccess = Boolean(
    checkAcessRights(location.pathname, INSERT_ACCESS),
  );

  // Redux state
  const StateList = useAppSelector((state) => state.state?.StateList || []);
  const EmailTemplateList = useAppSelector(
    (state) => state.emailTemplate?.EmailTemplateList || [],
  );
  const templateLoading = useAppSelector(
    (state) => state.emailTemplate?.loading || false,
  );
  const ContactList = useAppSelector(
    (state) => state.bulkEmail?.ContactList || [],
  );
  const loading = useAppSelector((state) => state.bulkEmail?.loading || false);
  const sending = useAppSelector((state) => state.bulkEmail?.sending || false);

  // Local selection & filter state
  const [selectedStateID, setSelectedStateID] = useState<number | null>(null);
  const [selectedTemplateID, setSelectedTemplateID] = useState<number | null>(
    null,
  );
  const [customSubject, setCustomSubject] = useState<string>("");
  const [selectedGridRows, setSelectedGridRows] = useState<{
    [key: string]: boolean | number[];
  }>({});
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(
    undefined,
  );
  const [page, setPage] = useState({ skip: 0, take: 10 });

  // Initial load
  useEffect(() => {
    dispatch(getAllActiveStates());
    dispatch(getAllActiveEmailTemplate()).then((res: any) => {
      if (!res.payload || res.payload.length === 0) {
        dispatch(getAllEmailTemplate());
      }
    });
    dispatch(findContactsByState({ StateID: 0 }));
    return () => {
      dispatch(clearBulkEmailState());
    };
  }, [dispatch]);

  // Reset selection when ContactList reference changes
  useEffect(() => {
    setSelectedGridRows({});
    setFilter(undefined);
    setPage({ skip: 0, take: 10 });
  }, [ContactList]);

  // Contact list with computed FullName for searching
  const dataWithFullName = useMemo(() => {
    if (!ContactList) return [];
    return ContactList.map((item) => ({
      ...item,
      FullName: `${item.FirstName || ""} ${item.LastName || ""}`.trim(),
    }));
  }, [ContactList]);

  // Filtered contacts list
  const filteredContactList = useMemo(() => {
    if (!dataWithFullName) return [];
    if (!filter) return dataWithFullName;
    return filterBy(dataWithFullName, filter);
  }, [dataWithFullName, filter]);

  // Page sliced contacts
  const pagedContactList = useMemo(() => {
    return filteredContactList.slice(page.skip, page.skip + page.take);
  }, [filteredContactList, page]);

  // Dropdown options
  const stateOptions = useMemo(
    () =>
      StateList?.map((s: any) => ({
        value: s.ID,
        label: s.StateName,
      })) || [],
    [StateList],
  );

  const emailTemplateOptions = useMemo(
    () =>
      EmailTemplateList?.map((t: any) => ({
        value: t.ID,
        label: t.Subject,
        raw: t,
      })) || [],
    [EmailTemplateList],
  );

  // Selected Email Template object
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateID || !EmailTemplateList) return null;
    return (
      EmailTemplateList.find(
        (t: any) =>
          t.ID === selectedTemplateID ||
          String(t.ID) === String(selectedTemplateID),
      ) || null
    );
  }, [selectedTemplateID, EmailTemplateList]);

  // Sync custom subject when template changes
  useEffect(() => {
    if (selectedTemplate) {
      setCustomSubject(selectedTemplate.Subject || "");
    } else {
      setCustomSubject("");
    }
  }, [selectedTemplate]);

  const handleStateChange = (event: any) => {
    const val = event?.value ?? null;
    setSelectedStateID(val);
    setSelectedGridRows({});
    setFilter(undefined);
    setPage({ skip: 0, take: 10 });
    if (val) {
      dispatch(findContactsByState({ StateID: val }));
    } else {
      dispatch(findContactsByState({ StateID: 0 }));
    }
  };

  const handleTemplateChange = (
    event: any,
    formRenderProps?: FormRenderProps,
  ) => {
    const rawVal = event?.value ?? event?.target?.value ?? event;
    let targetId = rawVal;
    if (typeof rawVal === "object" && rawVal !== null) {
      targetId = rawVal.value ?? rawVal.ID ?? null;
    }
    setSelectedTemplateID(targetId);

    const found = EmailTemplateList?.find(
      (t: any) => t.ID === targetId || String(t.ID) === String(targetId),
    );
    const newSubject = found?.Subject || "";
    setCustomSubject(newSubject);
    if (formRenderProps) {
      formRenderProps.onChange("TemplateID", { value: rawVal });
      formRenderProps.onChange("Subject", { value: newSubject });
    }
  };

  const handleFilterChange = useCallback((e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage((prev) => ({ ...prev, skip: 0 }));
  }, []);

  const handlePageChange = useCallback((event: GridPageChangeEvent) => {
    setPage(event.page);
  }, []);

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedGridRows,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedGridRows(newSelectedState);
    },
    [selectedGridRows],
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: { [key: string]: boolean | number[] } = {
        ...selectedGridRows,
      };

      pagedContactList.forEach((item: any) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedGridRows(newSelectedState);
    },
    [pagedContactList, selectedGridRows],
  );

  const toggleSelectAllFiltered = useCallback(() => {
    const allSelected =
      filteredContactList.length > 0 &&
      filteredContactList.every(
        (item) => selectedGridRows[idGetter(item)] === true,
      );
    const newSelectedState: { [key: string]: boolean | number[] } = {
      ...selectedGridRows,
    };
    filteredContactList.forEach((item) => {
      newSelectedState[idGetter(item)] = !allSelected;
    });
    setSelectedGridRows(newSelectedState);
  }, [filteredContactList, selectedGridRows]);

  const selectedCount = useMemo(() => {
    return (ContactList || []).filter(
      (item) => selectedGridRows[idGetter(item)] === true,
    ).length;
  }, [ContactList, selectedGridRows]);

  const firstSelectedContact = useMemo(() => {
    return (
      (ContactList || []).find(
        (item) => selectedGridRows[idGetter(item)] === true,
      ) || null
    );
  }, [ContactList, selectedGridRows]);

  // Extract variables like {{1}}, {{ 2 }} from template text
  const templateVariables = (templateText: string) => {
    if (!templateText) return [];
    const regex = /\{\{\s*(\d+)\s*\}\}/g;
    const matches: number[] = [];
    let match;
    while ((match = regex.exec(templateText)) !== null) {
      const num = Number(match[1]);
      if (!matches.includes(num)) {
        matches.push(num);
      }
    }
    return matches;
  };

  const activeVariables = useMemo(() => {
    const subjectText = customSubject || selectedTemplate?.Subject || "";
    const contentText = selectedTemplate?.Content || "";
    return templateVariables(`${subjectText} ${contentText}`);
  }, [selectedTemplate, customSubject]);

  const getFormValues = (renderProps: FormRenderProps) => {
    const values: any = {
      StateID: renderProps.valueGetter("StateID"),
      TemplateID: renderProps.valueGetter("TemplateID"),
      Subject: renderProps.valueGetter("Subject") || customSubject,
    };
    activeVariables.forEach((v) => {
      values[`Variable${v}`] = renderProps.valueGetter(`Variable${v}`);
    });
    return values;
  };

  const handleGetSuggestions = async () => {
    // Dynamic filter options are handled cleanly in FormSelectionField filterData without keystroke accumulation
  };

  const resolveVariableValue = (rawVal: any, contact: any) => {
    let valStr = "";
    if (typeof rawVal === "object" && rawVal !== null) {
      valStr = String(rawVal.value ?? rawVal.label ?? "");
    } else if (rawVal !== undefined && rawVal !== null) {
      valStr = String(rawVal);
    }
    const lower = valStr.toLowerCase().trim();
    if (lower === "[name]") {
      return `${contact?.FirstName || ""} ${contact?.LastName || ""}`.trim();
    } else if (lower === "[mobilenumber]") {
      return String(contact?.MobileNo || "");
    } else if (lower === "[emailid]") {
      return String(contact?.EmailID || "");
    } else if (lower === "[cityname]") {
      return String(contact?.CityName || "");
    } else if (lower === "[referalcode]") {
      return String(contact?.ReferalCode || "");
    }
    return valStr;
  };

  const handleSendEmail = async (values: any) => {
    if (!isSendAccess) {
      ErrorToast("You do not have rights to send Bulk Emails!");
      return;
    }
    if (!selectedTemplate) {
      ErrorToast("Please select an Email Template!");
      return;
    }

    const subjectVal = (customSubject || values?.Subject || "").trim();
    if (!subjectVal) {
      ErrorToast("Please enter an Email Subject!");
      return;
    }

    const selectedContacts = (ContactList || [])
      .filter((item) => selectedGridRows[idGetter(item)] === true)
      .filter((item) => item.EmailID && String(item.EmailID).trim() !== "");

    if (selectedContacts.length === 0) {
      ErrorToast(
        "Please select at least one contact with a valid email address!",
      );
      return;
    }

    if (activeVariables.length > 0) {
      const missingVars = activeVariables.filter((varNum) => {
        const val = values?.[`Variable${varNum}`];
        if (!val) return true;
        if (typeof val === "object" && val !== null) {
          return (
            !val.value &&
            !val.label &&
            (typeof val.value !== "string" || val.value.trim() === "")
          );
        }
        return String(val).trim() === "";
      });

      if (missingVars.length > 0) {
        ErrorToast(
          missingVars.length === 1
            ? `Please select a value for Variable ${missingVars[0]}`
            : `Please select values for variables: ${missingVars.join(", ")}`,
        );
        return;
      }
    }

    const emailList = selectedContacts.map((contact) => {
      const vars: string[] = [];
      for (const varNum of activeVariables) {
        const selectedVariable = values?.[`Variable${varNum}`];
        const resolved = resolveVariableValue(selectedVariable, contact);
        vars.push(resolved);
      }

      return {
        Email: String(contact.EmailID || "").trim(),
        Variables: vars,
      };
    });

    const payload: ISendBulkEmailPayload = {
      TemplateID: Number(selectedTemplateID || 0),
      EmailList: emailList,
    };

    const response = await dispatch(sendBulkEmail(payload));
    if (response?.meta?.requestStatus === "fulfilled") {
      setSelectedGridRows({});
    }
  };

  const renderPreviewWithVariables = (
    text: string,
    formRenderProps: FormRenderProps,
  ) => {
    if (!text) return "";
    let result = text;

    activeVariables.forEach((varNum) => {
      const varVal = formRenderProps.valueGetter(`Variable${varNum}`);
      let displayVal = "";
      if (typeof varVal === "object" && varVal !== null) {
        displayVal = String(varVal.value ?? varVal.label ?? "");
      } else if (varVal !== undefined && varVal !== null) {
        displayVal = String(varVal);
      }

      if (displayVal) {
        const regex = new RegExp(`\\{\\{\\s*${varNum}\\s*\\}\\}`, "g");
        result = result.replace(regex, displayVal);
      }
    });

    return result;
  };

  return (
    <div
      style={{
        boxSizing: "border-box",
      }}
    >
      {(loading || sending) && <LoadingPanel gridRef={gridRef} />}

      {/* Top Header */}
      <BulkEmailHeader
        selectedCount={selectedCount}
        selectedTemplateSubject={selectedTemplate?.Subject}
        isSendAccess={isSendAccess}
      />

      {/* Main Dual-Pane Workspace */}
      <Form
        initialValues={{
          StateID: selectedStateID,
          TemplateID: selectedTemplateID,
          Subject: customSubject,
        }}
        onSubmit={handleSendEmail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr minmax(350px, 1fr)",
                gap: "20px",
                alignItems: "start",
              }}
            >
              {/* LEFT COLUMN: Recipient Selection Grid */}
              <ContactSelectionSection
                contactList={ContactList}
                filteredContactList={filteredContactList}
                pagedContactList={pagedContactList}
                selectedGridRows={selectedGridRows}
                filter={filter}
                page={page}
                onFilterChange={handleFilterChange}
                onPageChange={handlePageChange}
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                onToggleSelectAllFiltered={toggleSelectAllFiltered}
                stateOptions={stateOptions}
                selectedStateID={selectedStateID}
                onStateChange={handleStateChange}
              />

              {/* RIGHT COLUMN: Configuration Form & Live Preview */}
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <EmailComposerCard
                  stateOptions={stateOptions}
                  emailTemplateOptions={emailTemplateOptions}
                  selectedTemplate={selectedTemplate}
                  activeVariables={activeVariables}
                  customSubject={customSubject}
                  selectedCount={selectedCount}
                  sending={sending}
                  isSendAccess={isSendAccess}
                  options={options}
                  onStateChange={handleStateChange}
                  onTemplateChange={handleTemplateChange}
                  onSubjectChange={(val, renderProps) => {
                    setCustomSubject(val);
                    renderProps.onChange("Subject", { value: val });
                  }}
                  onGetSuggestions={handleGetSuggestions}
                  onSubmit={handleSendEmail}
                  getFormValues={getFormValues}
                  formRenderProps={formRenderProps}
                />

                {/* Floating Gmail-Style Compose Drawer Widget at Bottom-Right */}
                <EmailLivePreview
                  selectedTemplate={selectedTemplate}
                  templateLoading={templateLoading}
                  customSubject={customSubject}
                  selectedCount={selectedCount}
                  firstSelectedContact={firstSelectedContact}
                  activeVariables={activeVariables}
                  formRenderProps={formRenderProps}
                  renderPreviewWithVariables={renderPreviewWithVariables}
                  onSendClick={() =>
                    handleSendEmail(getFormValues(formRenderProps))
                  }
                  sending={sending}
                  isSendAccess={isSendAccess}
                  isFloating={true}
                />
              </div>
            </div>
          </FormElement>
        )}
      />
    </div>
  );
};

export default BulkEmail;
